import React from "react";
import { connect } from "react-redux";
import { withTranslation } from "react-i18next";
import moment from "moment";
import Avatar from "components/Avatar";
import {
	FiCalendar,
	FiMessageSquare,
	FiMoreHorizontal,
	FiPaperclip,
} from "react-icons/fi";
import { Dropdown } from "react-bootstrap";
import * as workflowsActions from "actions/workflowsActions";
import { workflowsService } from "services";
import { wfStatus } from "services/workflowsService";
import { getDiffDays } from "utils/datetime";

const STATUS_FILTERS = [
	{ key: "all", translation: "absences.statusFilters.all" },
	{ key: "pending", translation: "absences.statusFilters.pending" },
	{ key: "approved", translation: "absences.statusFilters.approved" },
	{ key: "denied", translation: "absences.statusFilters.denied" },
	{ key: "canceled", translation: "absences.statusFilters.canceled" },
];

class MyAbsences extends React.Component {
	state = {
		statusFilter: "all",
	};

	getEmptyRow = () => (
		<p className="no-results">{this.props.t("absences.noResults")}</p>
	);

	getStatusKey = (status) => {
		if (
			status === wfStatus.pending ||
			status === wfStatus.pendingSecondLevel
		) {
			return "pending";
		}
		if (status === wfStatus.approved) return "approved";
		if (status === wfStatus.denied) return "denied";
		if (status === wfStatus.canceled) return "canceled";
		return "pending";
	};

	getStatusClass = (status) => {
		switch (this.getStatusKey(status)) {
			case "approved":
				return "estado-si";
			case "denied":
				return "estado-no";
			case "canceled":
				return "estado-anulado";
			default:
				return "estado-pendiente";
		}
	};

	getDateLabel = (absence) => {
		const start = moment(absence.start);
		const end = absence.end ? moment(absence.end) : null;
		const startLabel = start.format("D MMM");

		if (!end || start.isSame(end, "day")) return startLabel;
		return startLabel + " - " + end.format("D MMM");
	};

	getDurationLabel = (absence) => {
		if (absence.workflow_hourly_requested) {
			return String(absence.duration || 0).replace(".", ",") + " h";
		}

		let days = absence.effective_days || 0;
		if (days === 0) days = getDiffDays(absence.start, absence.end);
		return days + " d";
	};

	getAbsenceCode = (absence) => {
		const name =
			(absence.shift && absence.shift.name) || absence.shift_type || "";
		const words = name.match(/[\p{L}\p{N}]+/gu) || [];
		return words
			.slice(0, 3)
			.map((word) => word.charAt(0))
			.join("")
			.toUpperCase();
	};

	getTypeColor = (absence) => {
		const shift = absence.shift || {};
		return shift.border_color || shift.background_color || "#5a49ff";
	};

	getTypeBackground = (color) => {
		if (/^#[0-9a-f]{6}$/i.test(color)) return color + "18";
		return "#f4f2ff";
	};

	downloadAttachment = async (absence, attachment) => {
		const response = await workflowsService.getAttachment(
			absence.id,
			attachment.url
		);
		const url = window.URL.createObjectURL(response);
		const link = document.createElement("a");
		link.href = url;
		link.download = attachment.name;
		document.body.appendChild(link);
		link.click();
		link.remove();
		window.URL.revokeObjectURL(url);
	};

	getActions = (absence) => (
		<Dropdown drop="up">
			<Dropdown.Toggle
				variant="action"
				aria-label={this.props.t("common.actions")}
			>
				<FiMoreHorizontal />
			</Dropdown.Toggle>
			<Dropdown.Menu>
				{absence.status === wfStatus.pending ? (
					<Dropdown.Item
						as="button"
						onClick={() => this.props.openEditAbsenceModal(absence)}
					>
						{this.props.t("absences.editAction")}
					</Dropdown.Item>
				) : (
					<Dropdown.Item
						as="button"
						onClick={() => this.props.openEditAbsenceModal(absence, true)}
					>
						{this.props.t("absences.viewAction")}
					</Dropdown.Item>
				)}

				{absence.status !== wfStatus.denied &&
					absence.status !== wfStatus.canceled && (
						<Dropdown.Item
							as="button"
							onClick={() => this.props.handleCancelRequest(absence)}
						>
							{this.props.t("absences.cancelRequest")}
						</Dropdown.Item>
					)}

				{absence.status !== wfStatus.pending && (
					<Dropdown.Item
						as="button"
						onClick={() => this.props.handleReopenRequest(absence)}
					>
						{this.props.t("absences.reopenRequest")}
					</Dropdown.Item>
				)}
			</Dropdown.Menu>
		</Dropdown>
	);

	getRows = () => {
		const visibleAbsences = this.props.employee_absences.filter(
			(absence) =>
				this.state.statusFilter === "all" ||
				this.getStatusKey(absence.status) === this.state.statusFilter
		);

		if (visibleAbsences.length === 0) return this.getEmptyRow();

		return visibleAbsences.map((absence) => {
			const statusKey = this.getStatusKey(absence.status);
			const typeColor = this.getTypeColor(absence);
			const shiftName =
				(absence.shift && absence.shift.name) || absence.shift_type;

			return (
				<article
					className={
						"my-absence-card" +
						(statusKey === "pending" ? " is-pending" : "")
					}
					key={absence.id}
				>
					<div className="my-absence-card-date">
						<span>
							<FiCalendar aria-hidden="true" />
							<strong>{this.getDateLabel(absence)}</strong>
						</span>
						<span
							className={
								"estado absence-status-pill " +
								this.getStatusClass(absence.status)
							}
						>
							{absence.status}
						</span>
					</div>

					<div className="my-absence-card-type">
						<span
							className="my-absence-code"
							style={{
								borderColor: typeColor,
								backgroundColor: this.getTypeBackground(typeColor),
							}}
						>
							{this.getAbsenceCode(absence)}
						</span>
						<span className="my-absence-name">{shiftName}</span>
						<span className="my-absence-duration">
							· {this.getDurationLabel(absence)}
						</span>
					</div>

					{absence.answerer && (
						<div className="my-absence-card-manager">
							<span className="my-absence-card-manager-label">
								{this.props.t("absences.managedBy")}
							</span>
							<Avatar
								userId={absence.answerer.id}
								className="avatar-3xs"
								alt={absence.answerer.name}
							/>
							<strong>
								{absence.answerer.name} {absence.answerer.surname}
							</strong>
						</div>
					)}

					{absence.answer_comments && (
						<div className="my-absence-card-answer">
							<FiMessageSquare aria-hidden="true" />
							<span>{absence.answer_comments}</span>
						</div>
					)}

					{absence.attachments && absence.attachments.length > 0 && (
						<div className="my-absence-card-attachments">
							{absence.attachments.map((attachment) => (
								<button
									type="button"
									key={attachment.url}
									onClick={() =>
										this.downloadAttachment(absence, attachment)
									}
									title={attachment.name}
								>
									<FiPaperclip aria-hidden="true" />
									<span>{attachment.name}</span>
								</button>
							))}
						</div>
					)}

					<div className="my-absence-card-footer">
						<span>
							{this.props.t("absences.requestedOn")}{" "}
							{moment(absence.requested).format("DD/MM/YYYY")}
						</span>
						<div className="my-absence-card-actions">
							{this.getActions(absence)}
						</div>
					</div>
				</article>
			);
		});
	};

	render() {
		if (this.props.employee_absences.length === 0) return this.getEmptyRow();

		return (
			<div className="my-absences-content">
				<div
					className="my-absences-status-filter"
					role="group"
					aria-label={this.props.t("absences.statusColumn")}
				>
					{STATUS_FILTERS.map((filter) => (
						<button
							type="button"
							key={filter.key}
							className={
								this.state.statusFilter === filter.key ? "is-active" : ""
							}
							aria-pressed={this.state.statusFilter === filter.key}
							onClick={() => this.setState({ statusFilter: filter.key })}
						>
							{this.props.t(filter.translation)}
						</button>
					))}
				</div>

				<div className="my-absences-list">{this.getRows()}</div>
			</div>
		);
	}
}

const mapStateToProps = (reducers) => {
	return {
		...reducers.shiftsReducer,
		...reducers.workflowsReducer,
		...reducers.employeesReducer,
		user: reducers.authReducer.user,
	};
};

export default connect(mapStateToProps, workflowsActions)(
	withTranslation()(MyAbsences)
);
